import type { CartesianPose } from '@sb/geometry';
import { convertEulerPose, ZERO_POSE } from '@sb/geometry';

import type { LocateMethod, ResultOrdering } from '../../vision/LocateTypes';
import {
  BlobDetection2DMethod,
  CustomCheckpointDetection2DMethod,
  DinoFastMethod,
  ShapeDetection2DMethod,
  TextPromptDetection2DMethod,
} from '../../vision/LocateTypes';

/**
 * Segmentation settings stored on the skill's `shared.matches` (MatchesConfig
 * in `@sb/ai`) rather than in `methodConfig`. Structural type so routine-runner
 * doesn't need to depend on the `@sb/ai` schema.
 */
export interface MatchesSegmentationConfig {
  enableSegmentationMask: boolean;
  segmentationMethod:
    | 'sam2'
    | 'guided_filter'
    | 'bilateral_solver'
    | 'heatmap_threshold';
}

/**
 * Converts a saved Locate skill's method name and raw methodConfig into the
 * LocateMethod discriminated union that runLocate consumes.
 *
 * Each branch delegates validation to the corresponding routine-runner Zod
 * schema so that any mismatched field is caught at call-time with a descriptive
 * parse error rather than silently producing wrong values.
 *
 * `matches` carries the segmentation settings the skill stores on
 * `shared.matches` (not in `methodConfig`). They only apply to DinoFast —
 * without them `DinoFastParams` falls back to its schema defaults
 * (mask on, sam2), silently ignoring what was saved on the skill.
 */
export function mapSkillMethodToLocateMethod(
  method: string,
  methodConfig: Record<string, unknown>,
  matches?: MatchesSegmentationConfig,
): LocateMethod {
  switch (method) {
    case 'dino_fast':
      return DinoFastMethod.parse({
        kind: 'DinoFast',
        settings: matches
          ? {
              ...methodConfig,
              enableSegmentationMask: matches.enableSegmentationMask,
              segmentationMethod: matches.segmentationMethod,
            }
          : methodConfig,
      });

    case 'blob':
      return BlobDetection2DMethod.parse({
        kind: 'BlobDetection2D',
        settings: methodConfig,
      });

    case 'shape_match':
      // The wizard saves a FLAT `ShapeMatchSkillConfig` (@sb/ai/types): the
      // Shape2DParams fields (minScale, maxScale, matchThreshold, …) plus a
      // peer `templateImage` key (`{ storageID }`). Lift `templateImage` out as
      // a sibling of `settings` to match the runtime `ShapeDetection2DMethod`.
      return ShapeDetection2DMethod.parse({
        kind: 'ShapeDetection2D',
        templateImage: methodConfig.templateImage,
        settings: methodConfig,
      });

    case 'text_prompt':
      return TextPromptDetection2DMethod.parse({
        kind: 'TextPromptDetection2D',
        settings: methodConfig,
      });

    case 'custom_checkpoint':
      return CustomCheckpointDetection2DMethod.parse({
        kind: 'CustomCheckpointDetection2D',
        settings: methodConfig,
      });

    default:
      throw new Error(
        `RunLocateSkill: unsupported locate method '${method}'. Supported: dino_fast, blob, shape_match, text_prompt, custom_checkpoint.`,
      );
  }
}

/**
 * Converts a saved Locate skill's projection transform into the `CartesianPose`
 * that runLocate's `transform` arg expects.
 *
 * The skill stores translation (`x`/`y`/`z`) in MILLIMETERS while runLocate (and
 * the rest of the geometry stack) works in METERS, so we scale by 1e-3. Rotation
 * (`roll`/`pitch`/`yaw`) is in degrees, which `convertEulerPose.toCartesian`
 * already expects, so it passes through unscaled.
 *
 * Note: the transform's `graspFrame` is intentionally NOT consumed here — it is
 * a reference *frame* (object / plane / world), not part of the offset pose.
 * `RunLocateSkill/Step.ts` reads it and forwards it to runLocate as
 * `graspFrame` alongside this offset.
 */
export function skillTransformToCartesian(
  transform: {
    x: number;
    y: number;
    z: number;
    roll: number;
    pitch: number;
    yaw: number;
  } | null,
): CartesianPose {
  if (!transform) {
    return ZERO_POSE;
  }

  return convertEulerPose.toCartesian({
    // Skill stores translation in mm; geometry stack uses meters.
    X: transform.x * 0.001,
    Y: transform.y * 0.001,
    Z: transform.z * 0.001,
    Rx: transform.roll,
    Ry: transform.pitch,
    Rz: transform.yaw,
  });
}

/**
 * Converts a saved Locate skill's MatchesConfig into the filter arguments that
 * runLocate accepts.
 */
export function mapMatchesToFilters(matches: {
  limitResults: number;
  orderBy: ResultOrdering;
}): { resultsLimit: number; resultOrdering: ResultOrdering } {
  return {
    resultsLimit: matches.limitResults,
    resultOrdering: matches.orderBy,
  };
}
